import { useState, useEffect } from "react";
import styled from "styled-components";
import dayjs from "dayjs";
import media from "styled-media-query";
import { ChallengeScheme } from "client-v2";

import {
	Text,
	Color,
	ProgressBar,
	ColorV2,
} from "@adoptaunabuelo/react-components";
import { Cake, Calendar, Check, PenLine, Rocket, Users2 } from "lucide-react";
import { useSelector } from "react-redux";
import { State } from "../../redux";

const screenSize2: any = "1024px";

const CellContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: 305px;
	min-width: 388px;
	border-radius: 12px;
	overflow: hidden;
	background-color: white;
	cursor: pointer;
	${media.lessThan("medium")`
        width: 100%;
        min-width: 100%;
    `}
`;
const Section = styled.div`
	padding: 24px;
	${media.lessThan(screenSize2)`
        padding: 20px;
    `}
`;
const Row = styled.div`
	display: flex;
	flex-direction: row;
`;
const Col = styled.div`
	display: flex;
	flex-direction: column;
`;
const IconCheckView = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 60px;
	background-color: white;
	padding: 2px 0px;
	width: fit-content;
	height: fit-content;
	margin-bottom: 16px;
`;
const IconCheck = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 20px;
	width: 20px;
	border-radius: 20px;
	background-color: ${ColorV2.surface.green};
	margin-right: 6px;
`;
const Image = styled.img`
	height: 109px;
	width: 109px;
	border-radius: 12px;
	object-fit: cover;
`;
const Chip = styled.div`
	display: flex;
	padding: 4px 8px;
	border-radius: 20px;
	border: 1px solid ${ColorV2.border.neutralSoft};
	width: fit-content;
	align-items: center;
	justify-content: center;
	gap: 6px;
`;

const ChallengeCell = (props: Props) => {
	const appLocation = useSelector((state: State) => state.location.location);
	const [pastDate, setPastDate] = useState(false);
	const [completed, setCompleted] = useState(false);

	useEffect(() => {
		//Check if past date
		const diff = dayjs(props.challenge.endDate.iso).diff(dayjs(), "days");
		if (diff <= 0) setPastDate(true);

		//Check if goal completed
		const restAmount = props.challenge.goal - props.challenge.amount;
		if (restAmount <= 0) setCompleted(true);
	}, [props.challenge]);

	const calculateAmount = (amount: number): string => {
		const tempAmount =
			amount / (appLocation ? appLocation.currency.conversion : 1);
		const tempCurrencyName = appLocation
			? appLocation.currency.name
			: "eur";
		const tempLanguage = appLocation ? appLocation.language : "es-ES";

		return new Intl.NumberFormat(tempLanguage, {
			style: "currency",
			currency: tempCurrencyName,
			maximumFractionDigits: 0,
		}).format(tempAmount);
	};

	const onCellClick = () => {
		if (props.challenge.type === "birthday") {
			window.open(
				`${window.location.origin}/cumpleanios?id=${props.challenge.objectId}`,
				"_blank"
			);
		} else if (props.challenge.type === "dream") {
			window.open(
				`${window.location.origin}/reto?id=${props.challenge.objectId}`
			);
		} else if (props.challenge.type === "signature") {
			window.open(
				`${window.location.origin}/firma?id=${props.challenge.objectId}`
			);
		}
	};

	return (
		<CellContainer onClick={onCellClick}>
			<Row style={{ padding: "24px 24px 0px", gap: 24 }}>
				<Col style={{ flex: 1, gap: 8 }}>
					<Chip>
						{props.challenge.type === "signature" ? (
							<PenLine
								height={20}
								width={20}
								color={ColorV2.text.neutralHard}
							/>
						) : props.challenge.type === "birthday" ? (
							<Cake
								height={20}
								width={20}
								color={ColorV2.text.neutralHard}
							/>
						) : (
							<Rocket
								height={20}
								width={20}
								color={ColorV2.text.neutralHard}
							/>
						)}
						<Text type="p2">
							{props.challenge.type === "signature"
								? "Firma"
								: props.challenge.type === "birthday"
								? "Cumpleaños"
								: "Sueño"}
						</Text>
					</Chip>
					<Text
						type="p"
						weight="medium"
						style={{
							display: "flex",
							height: 70,
							textOverflow: "ellipsis",
							wordWrap: "break-word",
							overflow: "hidden",
						}}
					>
						{props.challenge.title}
					</Text>
				</Col>
				<Image src={props.challenge.image?.url} />
			</Row>
			<Section>
				{completed ? (
					<IconCheckView>
						<IconCheck>
							<Check
								height={18}
								width={18}
								strokeWidth={2}
								color={"white"}
							/>
						</IconCheck>
						<Text type="p2" style={{ color: ColorV2.text.green }}>
							¡
							{props.challenge.type === "signature"
								? "Objetivo"
								: "Sueño"}{" "}
							cumplido!
						</Text>
					</IconCheckView>
				) : null}
				<ProgressBar
					style={{ height: 8, marginBottom: 8 }}
					progress={
						props.challenge.type === "signature"
							? [
									{
										value: completed
											? 0
											: props.amount
											? (props.amount /
													props.challenge.goal) *
											  100
											: 0,
										color: Color.text.primary,
									},
							  ]
							: [
									{
										value:
											(props.challenge.amount /
												props.challenge.goal) *
											100,
										color: completed
											? Color.status.color.success
											: props.challenge.type === "rose"
											? ColorV2.surface.redMedium
											: Color.text.primary,
									},
							  ]
					}
					animationTime={0.3}
					animationDelay={0.5}
				/>
				<Row style={{ alignItems: "flex-end", marginBottom: 24 }}>
					<Text
						type="h5"
						weight="medium"
						style={{
							color: completed
								? Color.status.color.success
								: props.challenge.type === "rose"
								? ColorV2.surface.redMedium
								: Color.text.primary,
						}}
					>
						{props.challenge.type === "signature"
							? props.challenge.amount
							: calculateAmount(props.challenge.amount)}
					</Text>
					<Text
						type="p2"
						style={{
							marginBottom: 1,
							marginLeft: 6,
							color: Color.text.high,
						}}
					>
						de{" "}
						{props.challenge.type === "signature"
							? props.challenge.goal + " firmas"
							: calculateAmount(props.challenge.goal)}
					</Text>
				</Row>
				<Col>
					{props.challenge.type !== "signature" && (
						<Row>
							<Users2
								style={{
									marginRight: 12,
									minHeight: 24,
									minWidth: 24,
								}}
								height={24}
								width={24}
								color={ColorV2.text.neutralHard}
							/>
							<Text
								type="p"
								style={{
									marginTop: 1,
								}}
							>
								{props.challenge.supporters} personas han
								colaborado
							</Text>
						</Row>
					)}
					{!completed && !pastDate && (
						<Row style={{ marginTop: 8 }}>
							<Calendar
								style={{
									marginRight: 12,
									minHeight: 24,
									minWidth: 24,
								}}
								height={24}
								width={24}
								color={ColorV2.text.neutralHard}
							/>
							<Text
								type="p"
								style={{
									marginTop: 1,
								}}
							>
								{dayjs(props.challenge.endDate.iso).diff(
									dayjs(),
									"days"
								) + "  días restantes "}
							</Text>
						</Row>
					)}
				</Col>
			</Section>
		</CellContainer>
	);
};
export default ChallengeCell;
export interface Props {
	challenge: ChallengeScheme;
	amount?: number;
	onClick?: () => void;
}
